import parse from "html-react-parser"
import { useState } from "react"

import iconAction from "images/icons/icon-action.svg?raw"
import iconDelay from "images/icons/icon-delay.svg?raw"
import iconFilter from "images/icons/icon-filter.svg?raw"
import iconPolling from "images/icons/icon-polling.svg?raw"
import iconQuery from "images/icons/icon-query.svg?raw"
import iconRealtime from "images/icons/icon-realtime.svg?raw"

import type { AppletPreviewData, AppletPreviewSection, UIEvent, UIResponse } from "../types"
import { actionLabel, CardActions, CardButton, CardSettledAction, CardShell } from "./card_shell"
import styles from "./applet_preview_card.module.scss"

interface Props {
  ui: UIEvent
  response?: UIResponse
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

// Single source of truth for each step's type badge (icon + label). Kept in
// sync with the Ruby `tqa_type_badge` helper that drives the ERB render sites
// so the badges can't drift again — see SC-143968.
function stepBadge(section: AppletPreviewSection): { icon: string; label: string } {
  switch (section.step_kind) {
    case "trigger":
      return section.instant
        ? { icon: iconRealtime, label: "Realtime trigger" }
        : { icon: iconPolling, label: "Polling trigger" }
    case "query":
      return { icon: iconQuery, label: "Query" }
    case "filter":
      return { icon: iconFilter, label: "Filter" }
    case "delay":
      return { icon: iconDelay, label: "Delay" }
    default:
      return { icon: iconAction, label: "Action" }
  }
}

// The filter and delay rows aren't services, so IFE sends no icon_url/brand_color
// (a fixed color can't adapt to light/dark). Render a built-in glyph instead.
function builtinRowIcon(section: AppletPreviewSection): string | null {
  switch (section.step_kind) {
    case "filter":
      return iconFilter
    case "delay":
      return iconDelay
    default:
      return null
  }
}

// Connector word shown to the left of each row. Fixed per step_kind, except
// actions: the first action reads "Then", the rest "And".
const CONNECTOR_BY_KIND: Partial<Record<AppletPreviewSection["step_kind"], string>> = {
  trigger: "If",
  query: "With",
  filter: "When",
  delay: "Wait",
}

function connectorLabel(sections: AppletPreviewSection[], index: number): string {
  const fixed = CONNECTOR_BY_KIND[sections[index].step_kind]
  if (fixed) return fixed
  return index === sections.findIndex(s => s.step_kind === "action") ? "Then" : "And"
}

function brandStyle(section: AppletPreviewSection) {
  return section.brand_color ? { backgroundColor: `#${section.brand_color}` } : undefined
}

const AppletPreviewCard = ({ ui, onRespond, response }: Props) => {
  const [answered, setAnswered] = useState(false)
  const { prompt: rawPrompt, sections } = ui.data as AppletPreviewData
  const prompt = rawPrompt || "Create Applet?"

  const confirmLabel = actionLabel(ui.actions, "select", "Confirm")
  const dismissLabel = actionLabel(ui.actions, "dismiss", "Make changes")

  const confirm = () => {
    if (answered) return
    setAnswered(true)
    onRespond(ui.id, "select", undefined, confirmLabel)
  }

  const dismiss = () => {
    if (answered) return
    setAnswered(true)
    onRespond(ui.id, "dismiss", undefined, dismissLabel)
  }

  const steps = (
    <ul className={styles["steps"]}>
      {sections.map((section, index) => {
        const badge = stepBadge(section)
        return (
        <li key={`${section.step_kind}-${section.value}`} className={styles["step"]}>
          <span className={styles["connector"]}>{connectorLabel(sections, index)}</span>
          <span className={styles["rail"]}>
            {index > 0 ? <span className={styles["line-top"]} /> : null}
            <span
              className={section.icon_url ? styles["icon"] : `${styles["icon"]} ${styles["icon-builtin"]}`}
              style={brandStyle(section)}
            >
              {section.icon_url ? (
                <img src={section.icon_url} alt={section.label} width="20" height="20" />
              ) : (
                builtinRowIcon(section) && parse(builtinRowIcon(section) as string)
              )}
            </span>
            {index < sections.length - 1 ? <span className={styles["line-bottom"]} /> : null}
          </span>
          <span className={styles["text"]}>
            <span className={styles["title"]}>{section.label}</span>
            <span className={styles["badge"]}>
              <span className={styles["badge-icon"]}>{parse(badge.icon)}</span>
              {badge.label}
            </span>
          </span>
        </li>
        )
      })}
    </ul>
  )

  if (response) {
    return (
      <CardShell>
        {steps}
        <CardSettledAction label={response.displayText ?? confirmLabel} confirmed={response.actionId === "select"} />
      </CardShell>
    )
  }

  return (
    <CardShell prompt={prompt} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      {steps}
      <CardActions>
        <CardButton variant="secondary" disabled={answered} onClick={dismiss}>
          {dismissLabel}
        </CardButton>
        <CardButton variant="primary" disabled={answered} onClick={confirm}>
          {confirmLabel}
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default AppletPreviewCard
